import { TransferTemplate } from "model";


/**
* @class MakeTransferUI
* @classdesc Class definition for controlling what elements are rendered during make transfer workflow
* @param {makeTransfer} transfer - instance of makeTransfer representing the state of current transfer
*/

//::constructor
export default class MakeTransferUI {
    _transfer;

    //::constructor makeTransfer -> this     
    constructor(transfer, branding) {
        const { options } = branding ?? {};
        const hasTransferTemplates = options ? options.transfer_templates_abi22 === "true" : false;
        const makeTransferObj = hasTransferTemplates ? TransferTemplate.init({ id: "prompt", name: "Open Transfer" }) : "";
        
        if(hasTransferTemplates) {
            transfer._template = transfer._template instanceof TransferTemplate ? transfer._template : makeTransferObj;
        } else {
            transfer._template = "";
        }

        this._transfer = transfer;
    }

    /** 
    * @name template
    * @instance
    * @property get {BOOLEAN} - if the from element should be rendered
    * @note returns true if a template is set.
    * @memberof MakeTransferUI 
    */
    get template() {
        return this._transfer.template === "" ? false : true;
    }

    /** 
    * @name templateOnly
    * @instance
    * @property get {BOOLEAN} - if the from element should be rendered
    * @note returns true if a templateOnly is set.
    * @memberof MakeTransferUI 
    */
    get templateOnly() {
        return this._transfer.templateOnly;
    }

    /** 
    * @name fromTemplateAccount
    * @instance
    * @property get {BOOLEAN} - if the fromTemplateAccount element should be rendered
    * @note returns true if a template is set.
    * @memberof MakeTransferUI 
    */
    get fromTemplateAccount() {
        if(this._transfer.template.id !== "prompt" && this._transfer.template.id !== undefined) {
            return true;
        }
        return false;
    }

    /** 
    * @name toTemplateAccount
    * @instance
    * @property get {BOOLEAN} - if the toTemplateAccount element should be rendered
    * @note returns true if a template is set.
    * @memberof MakeTransferUI 
    */
    get toTemplateAccount() {
        if(this._transfer.template.id !== "prompt" && this._transfer.template.id !== undefined) {
            return true;
        }
        return false;
    }

    /** 
    * @name editFromAccount
    * @instance
    * @property get {BOOLEAN} - if the "edit from" element should be rendered
    * @memberof MakeTransferUI 
    */
    get editFromAccount() {
        if(this._transfer.id !== "" && this._transfer.id !== undefined) {
            return true;
        }
        return false;
    }

    /** 
    * @name editToAccount
    * @instance
    * @property get {BOOLEAN} - if the "edit to" element should be rendered
    * @memberof MakeTransferUI 
    */
    get editToAccount() {
        if(this._transfer.id !== "" && this._transfer.id !== undefined) {
            return true;
        }
        return false;
    }

    /** 
    * @name from
    * @instance
    * @property get {BOOLEAN} - if the from element should be rendered
    * @note this always returns true because it is always shown
    * @memberof MakeTransferUI 
    */
    get from() {
        if(this._transfer.template.id !== "prompt" && this._transfer.template.id !== undefined) {
            return false;
        }
        return true;
    }

    /** 
     * @name to
     * @instance
     * @property get {BOOLEAN} - if the to element should be rendered
     * @note this always returns true because it is always shown
     * @memberof MakeTransferUI 
     */
    get to() {
        if(this._transfer.template.id !== "prompt" && this._transfer.template.id !== undefined) {
            return false;
        }
        return true;
    }

    /** 
     * @name paymentOption
     * @instance
     * @property get {STRING} - if the paymentOption element should be rendered
     * @memberof MakeTransferUI 
     */
    get paymentOption() {
        const isLoan = this._transfer.to.type === "loan";
        return this._transfer.to !== undefined ? isLoan : false;
    }

    /** 
     * @name amount
     * @instance
     * @property get {BOOLEAN} - if the amount element should be rendered
     * @memberof MakeTransferUI 
     */
    get amount() {
        if (this._transfer.to !== "") {
            if (this._transfer.to.type !== "loan") {
                return true;
            }
            return (this._transfer.paymentOption !== "Payment Due"&& this._transfer.paymentOption !== "Entire Balance") && this._transfer.paymentOption !=="";
        }
        return true;
    }

    /** 
     * @name templateMaxAmount
     * @instance
     * @property get {BOOLEAN} - if the templateMaxAmount element should be rendered
     * @memberof MakeTransferUI 
     */
    get templateMaxAmount() {
        if(this._transfer.template.id !== "prompt" && this._transfer.template.id !== undefined) {
            return true;
        }
        return false;
    }

    /** 
     * @name schedule
     * @instance
     * @property get {BOOLEAN} - if the schedule element should be rendered
     * @note this always returns true because it is always shown
     * @memberof MakeTransferUI 
     */
    get schedule() {
        return true;
    }

    /** 
     * @name sendOn
     * @instance
     * @property get {BOOLEAN} - if the sendOn element should be rendered
     * @memberof MakeTransferUI 
     */
    get sendOn() {
        return this._transfer.schedule === "Future (One Time)";
    }

    /** 
    * @name frequency
    * @instance
    * @property get {BOOLEAN} - if the frequency element should be rendered
    * @memberof MakeTransferUI 
    */
    get frequency() {
        return this._transfer.schedule === "Recurring";
    }

    /** 
    * @name firstTransferDay
    * @instance
    * @property get {BOOLEAN} - if the firstTransferDay element should be rendered
    * @memberof MakeTransferUI 
    */
    get firstTransferDay() {
        const isRecurring = this._transfer.schedule === "Recurring";
        const isTwice = this._transfer.frequency === "Twice a month";
        return isRecurring && isTwice;
    }

    /** 
    * @name secondTransferDay
    * @instance
    * @property get {BOOLEAN} - if the secondTransferDay element should be rendered
    * @memberof MakeTransferUI 
    */
    get secondTransferDay() {
        const isRecurring = this._transfer.schedule === "Recurring";
        const isTwice = this._transfer.frequency === "Twice a month";
        return isRecurring && isTwice;
    }

    /** 
    * @name startDate
    * @instance
    * @property get {BOOLEAN} - if the startDate element should be rendered
    * @memberof MakeTransferUI 
    */
    get startDate() {
        return this._transfer.schedule === "Recurring";
    }

    /** 
    * @name estimatedDeliveryDate
    * @instance
    * @property get {BOOLEAN} - if the estimatedDeliveryDate element should be rendered
    * @memberof MakeTransferUI 
    */
    get estimatedDeliveryDate() {
        const hasSendOn = this._transfer.sendOn !== "" && this._transfer.sendOn !== undefined;
        const hasStartDate = this._transfer.startDate !== "" && this._transfer.startDate !== undefined;
        const notOneTime = this._transfer.schedule !== "Immediate (One Time)";
        return (hasSendOn||hasStartDate) && notOneTime;
    }

    /** 
    * @name duration
    * @instance
    * @property get {BOOLEAN} - if the duration element should be rendered
    * @memberof MakeTransferUI 
    */
    get duration() {
        return this._transfer.schedule === "Recurring";
    }

    /** 
    * @name endDate
    * @instance
    * @property get {BOOLEAN} - if the endDate element should be rendered
    * @memberof MakeTransferUI 
    */
    get endDate() {
        const isRecurring = this._transfer.schedule === "Recurring";
        const isSpecifiedDate = this._transfer.duration === "End by a specified date";
        return isRecurring && isSpecifiedDate;
    }

    /** 
    * @name numberOfTransfers
    * @instance
    * @property get {BOOLEAN} - if the numberOfTransfers element should be rendered
    * @memberof MakeTransferUI 
    */
    get numberOfTransfers() {
        const isRecurring = this._transfer.schedule === "Recurring";
        const isNumberOfTransfers = this._transfer.duration === "End after a specified number of transfers"
        return isRecurring && isNumberOfTransfers;
    }

    /** 
    * @name description
    * @instance
    * @property get {BOOLEAN} - if the description element should be rendered
    * @memberof MakeTransferUI 
    */
    get description() {
        if(this._transfer.template.id !== "prompt" && this._transfer.template.id !== undefined) {
            return true;
        }
        return this._transfer.to !== "";
    }

    /** 
    * @name sendEmail
    * @instance
    * @property get {BOOLEAN} - if the sendEmail element should be rendered
    * @memberof MakeTransferUI 
    */
    get sendEmail() {
        const isRecurring = this._transfer.schedule === "Recurring";
        const isFuture = this._transfer.schedule === "Future (One Time)";
        return isRecurring || isFuture;
    }

    /** 
    * @name isEditingTransfer
    * @instance
    * @property get {BOOLEAN} - Tells us if this transfer is being edited.
    * @memberof MakeTransferUI 
    */
    get isEditingTransfer() {
        return this._transfer.id !== "" && this._transfer.id !== undefined;
    }

    /**
     * 
     * @param {makeTransfer} transfer 
     * @returns this
     * static factory method for initializing  MakeTransferUI  
     */
    static init(transfer, branding) {
        return new MakeTransferUI(transfer, branding);
    }
}